import { useState } from "react";
import { Link } from "react-router-dom";
import { Bell, X, Loader2 } from "lucide-react";
import { Button } from "./ui/button";
import { useLanguage } from "../contexts/LanguageContext";
import { toast } from "sonner";

const SESSION_KEY = "push_banner_hidden";

/**
 * Lightweight reminder banner shown on the Messages inbox after the opt-in
 * modal has been dismissed. Offers an inline one-click activation plus a link
 * to Settings, and can be hidden for the current session.
 */
export function PushOptInBanner({ onEnable }) {
	const { language } = useLanguage();
	const [busy, setBusy] = useState(false);
	const [hidden, setHidden] = useState(() => {
		try {
			return sessionStorage.getItem(SESSION_KEY) === "1";
		} catch {
			return false;
		}
	});

	if (hidden) return null;

	const hideForSession = () => {
		try {
			sessionStorage.setItem(SESSION_KEY, "1");
		} catch {
			/* ignore */
		}
		setHidden(true);
	};

	const handleEnable = async () => {
		if (busy) return;
		setBusy(true);
		try {
			await onEnable();
			toast.success(
				language === "it"
					? "Notifiche push attivate"
					: "Push notifications enabled",
			);
		} catch (e) {
			toast.error(
				e?.message ||
					(language === "it"
						? "Impossibile attivare le notifiche push"
						: "Could not enable push notifications"),
			);
		} finally {
			setBusy(false);
		}
	};

	return (
		<div className="mx-4 mb-2 flex items-center gap-3 rounded-sm border border-volt/30 bg-volt/5 px-3 py-2.5">
			<Bell className="h-5 w-5 shrink-0 text-volt" />
			<div className="min-w-0 flex-1">
				<p className="text-sm font-medium leading-tight">
					{language === "it"
						? "Attiva le notifiche push"
						: "Turn on push notifications"}
				</p>
				<p className="text-xs text-muted-foreground">
					{language === "it" ? (
						<>
							Non perdere messaggi e nuovi programmi.{" "}
							<Link to="/settings" className="text-volt hover:underline">
								Impostazioni
							</Link>
						</>
					) : (
						<>
							Don&apos;t miss messages and new programs.{" "}
							<Link to="/settings" className="text-volt hover:underline">
								Settings
							</Link>
						</>
					)}
				</p>
			</div>
			<Button
				size="sm"
				onClick={handleEnable}
				disabled={busy}
				className="shrink-0 rounded-sm bg-volt text-black font-bold hover:bg-volt/90"
			>
				{busy ? (
					<Loader2 className="h-4 w-4 animate-spin" />
				) : language === "it" ? (
					"Attiva"
				) : (
					"Enable"
				)}
			</Button>
			<button
				type="button"
				onClick={hideForSession}
				className="shrink-0 text-muted-foreground hover:text-foreground"
				aria-label={language === "it" ? "Chiudi" : "Dismiss"}
			>
				<X className="h-4 w-4" />
			</button>
		</div>
	);
}
